import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import type { components } from "@scalty/contracts";
import { apiFetch } from "./useApi";
import { qk } from "../utils/queryKeys";

export type PlatformSetting = components["schemas"]["PlatformSetting"];
type PlatformSettingList = components["schemas"]["PlatformSettingList"];

/**
 * The settings registry — every platform default that shapes the examples a
 * training run writes and the model it trains, with the provenance that makes it
 * accountable (GET /settings). One registry, so a surface that shows a default
 * shows one nobody has measured as `unmeasured` rather than as a chosen number.
 *
 * Env-global policy, not org data: cached for the session like the base-model
 * catalog.
 */
export function usePlatformSettings() {
  const query = useQuery({
    queryKey: qk.platformSettings(),
    queryFn: () => apiFetch<PlatformSettingList>("/settings"),
    staleTime: 60 * 60 * 1000,
  });

  /** One entry by key, or null while the registry is still loading. */
  const setting = (key: string) =>
    computed<PlatformSetting | null>(
      () => query.data.value?.settings.find((entry) => entry.key === key) ?? null,
    );

  return { query, setting };
}
